import type {TargetAndTransition, Transition} from 'framer-motion';
import {getAssetUrl} from '@Utils/s3.util';

export enum LayerRole {
  BACKGROUND = 'background',
  PHOTO = 'photo',
  TEXT = 'text',
}

export enum LayersVisualVariant {
  PROTIP = 'protip',
  LOADER = 'loader',
}

export enum LayersLoopMode {
  SEAMLESS = 'seamless',
  TIMED = 'timed',
}

export interface LayerOffset {
  x: string;
  y: string;
}

export interface LayerPoses {
  composed: TargetAndTransition;
  separated: TargetAndTransition;
}

interface AnimatedLayer {
  role: LayerRole;
  imageUrl: string;
  poses: LayerPoses;
}

export interface LayerTransitionTiming {
  delayMs: number;
  durationS: number;
}

export interface LayersAnimationTiming {
  toSeparated: LayerTransitionTiming;
  toComposed: LayerTransitionTiming;
}

export interface LayersAnimationConfig {
  layers: AnimatedLayer[];
  timing: LayersAnimationTiming;
  loopMode: LayersLoopMode;
}

export interface LayerMotion {
  animate: TargetAndTransition;
  transition: Transition;
}

export interface EditableTextLayersVisualProps {
  animation?: LayersAnimationConfig;
  variant?: LayersVisualVariant;
}

const LAYER_TRANSFORM = {rotate: 14, skewX: 13, scaleY: 0.97};
const TO_SEPARATED_DELAY_MS = 80;
const TO_COMPOSED_DELAY_MS = 200;
const PROTIP_TRANSITION_DURATION_S = 1.5;

const LAYER_ORIGIN: LayerOffset = {x: '0%', y: '0%'};

export const getLayerPoses = (separated: LayerOffset, composed: LayerOffset = LAYER_ORIGIN): LayerPoses => {
  return {
    composed: {...LAYER_TRANSFORM, ...composed},
    separated: {...LAYER_TRANSFORM, ...separated},
  };
};

export const getLayersAnimationTiming = (durationS: number): LayersAnimationTiming => {
  return {
    toSeparated: {delayMs: TO_SEPARATED_DELAY_MS, durationS},
    toComposed: {delayMs: TO_COMPOSED_DELAY_MS, durationS},
  };
};

export const PROTIP_LAYERS_ANIMATION: LayersAnimationConfig = {
  layers: [
    {role: LayerRole.BACKGROUND, imageUrl: getAssetUrl('ai-chat-editable-text-layers-bg.webp'), poses: getLayerPoses({x: '15%', y: '-26%'})},
    {role: LayerRole.TEXT, imageUrl: getAssetUrl('ai-chat-editable-text-layers-text.webp'), poses: getLayerPoses({x: '-14%', y: '28%'})},
  ],
  timing: getLayersAnimationTiming(PROTIP_TRANSITION_DURATION_S),
  loopMode: LayersLoopMode.TIMED,
};
